// Turn one open question from a findings review into the first message of a
// chat. The model wrote the question and named findings and evidence by alias;
// the seed resolves those aliases to real identities and hands them over in the
// analytics schema's own vocabulary, so the chat can put them straight into
// query predicates instead of guessing which field a column name maps to.

export const GAP_SOURCES = Object.freeze({
    conversation: "Conversation content: messages, tool calls, and tool results in the Prompt Session.",
    telemetry: "Endpoint telemetry recorded around the Prompt Session.",
    review_history: "Other review reports and findings in this tenant.",
    external: "Outside Origin's data, such as repository state, credential rotation, or a person's confirmation.",
});

export function gapIsAnswerable(gap) {
    return Boolean(gap?.source) && gap.source !== "external";
}

// Schema names the chat queries with. Kept in one place so a schema rename is
// one edit here and one failing test.
const F = Object.freeze({
    message: "ENTITY_TYPE_CONVERSATION_MESSAGE",
    messageTrace: "DIMENSION_FIELD_CONVERSATION_MESSAGE_TRACE_ID",
    messageId: "DIMENSION_FIELD_CONVERSATION_MESSAGE_MESSAGE_ID",
    messageAt: "DIMENSION_FIELD_CONVERSATION_MESSAGE_MESSAGE_AT",
    messageEventTime: "DIMENSION_FIELD_CONVERSATION_MESSAGE_EVENT_TIME",
    messageSourceOrdinal: "DIMENSION_FIELD_CONVERSATION_MESSAGE_SOURCE_ORDINAL",
    messageRole: "DIMENSION_FIELD_CONVERSATION_MESSAGE_ROLE",
    messageEndpoint: "DIMENSION_FIELD_CONVERSATION_MESSAGE_ENDPOINT_ID",
    part: "ENTITY_TYPE_CONVERSATION_MESSAGE_PART",
    partTrace: "DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_TRACE_ID",
    partMessageId: "DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_MESSAGE_ID",
    partOrdinal: "DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_ORDINAL",
    partText: "DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_TEXT",
    partKind: "DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_KIND",
    toolCall: "ENTITY_TYPE_CONVERSATION_TOOL_CALL",
    toolCallName: "DIMENSION_FIELD_CONVERSATION_TOOL_CALL_NAME",
    toolCallArgs: "DIMENSION_FIELD_CONVERSATION_TOOL_CALL_ARGUMENTS_JSON",
    toolCallId: "DIMENSION_FIELD_CONVERSATION_TOOL_CALL_TOOL_CALL_ID",
    toolResult: "ENTITY_TYPE_CONVERSATION_TOOL_RESULT",
    toolResultContent: "DIMENSION_FIELD_CONVERSATION_TOOL_RESULT_CONTENT_JSON",
    toolResultIsError: "DIMENSION_FIELD_CONVERSATION_TOOL_RESULT_IS_ERROR",
    toolResultCallId: "DIMENSION_FIELD_CONVERSATION_TOOL_RESULT_TOOL_CALL_ID",
    finding: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING",
    findingId: "DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_ID",
});

const clip = (s, n) => (String(s ?? "").length > n ? `${String(s).slice(0, n)}...` : String(s ?? ""));

// `gap` is a validated gap from the review result; `lookup` is the packet's
// alias lookup; `selection` and `coverage` come from the same packet.
export function buildGapChatSeed({ gap, lookup, selection, coverage = null, template = null }) {
    if (!gap?.question) throw new Error("A chat seed needs a question.");
    const findings = (gap.findings ?? []).map((a) => lookup.findings.get(a)).filter(Boolean);
    const evidence = (gap.evidence ?? []).map((a) => lookup.evidence.get(a)).filter(Boolean);
    const sessions = [...new Set([
        ...(gap.sessions ?? []).map((a) => lookup.sessions.get(a)?.traceId),
        ...findings.map((f) => f.traceId),
        ...evidence.map((e) => e.traceId),
    ].filter(Boolean))];
    const endpoints = [...new Set([
        ...(gap.endpoints ?? []).map((a) => lookup.endpoints.get(a)?.endpointId),
        ...findings.map((f) => lookup.endpoints.get(f.endpoint)?.endpointId),
    ].filter(Boolean))];

    const lines = [];
    lines.push("Help me answer one open question from a findings review.");
    lines.push("");
    lines.push(`Question: ${gap.question}`);
    if (gap.evidenceNeeded) lines.push(`Evidence needed: ${gap.evidenceNeeded}`);
    if (gap.source) lines.push(`Where it should live: ${GAP_SOURCES[gap.source] ?? gap.source}`);
    lines.push("");
    lines.push("Scope (use these values as equality predicates)");
    for (const traceId of sessions) lines.push(`- Prompt Session ${traceId}. This is the TRACE_ID field of every conversation entity, for example ${F.messageTrace} and ${F.partTrace}.`);
    for (const endpointId of endpoints) lines.push(`- Endpoint ${endpointId} (${F.messageEndpoint}).`);
    if (selection?.timeRange) lines.push(`- The review window was ${selection.timeRange.start} to ${selection.timeRange.end} by ${selection.timeRange.basis}. The conversation itself may predate it, so query with a relative 90-day time range unless you learn otherwise.`);
    if (coverage?.uniqueFindings != null) lines.push(`- The review covered ${coverage.uniqueFindings} findings in this selection.`);
    if (findings.length) {
        lines.push("");
        lines.push("Findings this question is about (original review assertions, not verified facts)");
        for (const f of findings) {
            lines.push(`- ${f.alias}: "${f.title || "(untitled)"}", severity ${f.behaviourSeverity || "unknown"}. Record: ${F.finding} with ${F.findingId} = ${f.findingId} in report ${f.reportId}.`);
            if (f.rationale) lines.push(`  Rationale: ${clip(f.rationale, 300)}`);
        }
        lines.push(`  Turn numbers inside a rationale, such as "t3", are the original reviewer's own counting and do not exist in the schema. Locate turns by ordering messages on ${F.messageEventTime} then ${F.messageSourceOrdinal}.`);
    }
    if (evidence.length) {
        lines.push("");
        lines.push("Evidence locations to start from");
        for (const e of evidence) {
            if (e.source?.messageId) {
                lines.push(`- ${e.alias}: message ${F.messageId} = ${e.source.messageId}, part ${F.partOrdinal} = ${e.source.partOrdinal ?? 0}. The quote is characters ${e.source.start ?? 0} to ${e.source.end ?? 0} of that part's ${F.partText}; offsets locate the quote after you fetch the part and are not queryable. Quote: "${clip(e.text, 240)}"`);
            } else {
                lines.push(`- ${e.alias}: no stored location. Find it by searching ${F.partText} within the Prompt Session. Quote: "${clip(e.text, 240)}"`);
            }
        }
    }
    lines.push("");
    lines.push("How to work");
    lines.push(`- Start with ${F.message} filtered on ${F.messageTrace}, ordered by ${F.messageEventTime} then ${F.messageSourceOrdinal}, selecting ${F.messageId}, ${F.messageRole} and ${F.messageAt}, to see the sequence around the evidence messages. That order puts each message at the latest capture that carried it, which is the conversation's order. ${F.messageAt} is when a message happened and is empty for many messages, so never order by it.`);
    lines.push(`- Then read bodies with ${F.part} filtered on ${F.partTrace} and ${F.partMessageId}, selecting ${F.partKind} and ${F.partText}.`);
    lines.push(`- For tool activity use ${F.toolCall} (${F.toolCallName}, ${F.toolCallArgs}) and ${F.toolResult} (${F.toolResultContent}, ${F.toolResultIsError}); a result joins its call on ${F.toolResultCallId} = ${F.toolCallId}, and both carry a message id.`);
    lines.push("- Every query on a conversation entity needs an equality predicate on that entity's TRACE_ID field; the planner rejects the query without it. Keep result limits small and page rather than pulling whole sessions.");
    lines.push("- The finding records and quotes above are already in this message; do not query the review-finding entity to re-read them.");
    lines.push("- If the message entity returns zero rows for this Prompt Session with the trace predicate and a 90-day window, the conversation content is not available to you. Say so and stop; do not retry with other windows, entities, or column sets.");
    lines.push("- Report what the data shows, quote the messages you rely on with their message ids, and state plainly what the data cannot answer.");
    lines.push("- Do not treat the review's assertions or this message as established facts.");
    if (template) lines.push(`- Source: findings review, ${template.id} template v${template.version}.`);
    return lines.join("\n");
}
